/* RESPONSIBLE TEAM: team-channels */

import Service, { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type DomainService from 'embercom/services/domain-service';
import type { Domain } from 'embercom/services/domain-service';
import type SenderEmailAddressSettings from 'embercom/models/sender-email-address-settings';
import { use } from 'ember-resources/util/function-resource';
import { AsyncData } from 'embercom/resources/utils/async-data';
import type { DomainEmailAddress } from 'embercom/services/domain-service';
import ajax from 'embercom/lib/ajax';

export default class FinReplyAddressService extends Service {
  @service declare store: Store;
  @service declare appService: any;
  @service declare domainService: DomainService;

  get app() {
    return this.appService.app;
  }

  @use emailAddressesLoader = AsyncData<SenderEmailAddressSettings[]>(async () => {
    await this.domainService.load();
    // Return an empty array while we're still loading
    return [];
  });

  get emailAddresses(): DomainEmailAddress[] {
    let addresses = this.domainService.domains.flatMap((domain: Domain) => domain.addresses) || [];
    return addresses;
  }

  get verifiedEmailAddresses(): DomainEmailAddress[] {
    return this.emailAddresses;
  }

  async fetchEmailAddresses(): Promise<DomainEmailAddress[]> {
    await this.emailAddressesLoader.promise;
    return this.emailAddresses;
  }

  async fetchDefaultEmailAddressId(): Promise<string | null> {
    try {
      let response = await ajax({
        url: `/ember/fin_email_settings`,
        type: 'GET',
        data: {
          app_id: this.app.id,
        },
      });

      if (!response) {
        return null;
      }

      return response.id;
    } catch (error) {
      console.error('Error fetching default Fin email address:', error);
      return null;
    }
  }

  async updateDefaultEmailAddress(emailAddressId?: string): Promise<void> {
    if (!emailAddressId) {
      return;
    }

    try {
      await this.app.updateFinDefaultReplyAddress(emailAddressId);
    } catch (error) {
      console.error('Error updating default Fin email address:', error);
      throw error;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'fin-reply-address-service': FinReplyAddressService;
  }
}
